import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Trash2, Loader2, ShoppingBag } from "lucide-react";
import { useEffect, useState } from "react";
import { api, imgUrl, apiError } from "@/lib/api";
import { launchCashfree } from "@/lib/cashfreeCheckout";
import { applyClickToCart } from "@/lib/attribution";
import PayMethodPicker from "@/components/PayMethodPicker";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";
import { useAuth } from "@/context/AuthContext";

export default function Cart() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const { formatMoney, convert, currency, countries, country } = useLocale();
  const { user, updateProfile } = useAuth();
  const profile = user?.profile || {};

  const [addr, setAddr] = useState({
    name: profile.address_name || user?.name || "",
    line1: profile.address_line1 || "",
    line2: profile.address_line2 || "",
    city: profile.address_city || "",
    region: profile.address_region || "",
    postal: profile.address_postal || "",
    country: profile.address_country || profile.country || country || "US",
    phone: profile.address_phone || "",
  });
  const [saveAddr, setSaveAddr] = useState(true);
  const [provider, setProvider] = useState("");
  const set = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  useEffect(() => {
    applyClickToCart();
  }, []);

  useEffect(() => {
    const sid = params.get("graph_session");
    if (!sid) return;
    let cancelled = false;
    setImporting(true);
    api.post("/cart/from-graph", { session_id: sid })
      .then(() => {
        if (cancelled) return;
        window.dispatchEvent(new Event("cart-updated"));
        qc.invalidateQueries({ queryKey: ["cart"] });
        navigate("/cart", { replace: true });
        toast.success("Bag updated from the graph");
      })
      .catch((e) => {
        if (!cancelled) toast.error(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setImporting(false);
      });
    return () => { cancelled = true; };
  }, [params, qc, navigate]);

  const dest = (addr.country || "").toUpperCase();
  const countryName = (countries || []).find((c) => c.code === dest)?.name || dest;
  const { data, isLoading } = useQuery({
    queryKey: ["cart", dest],
    queryFn: async () => (await api.get("/cart", { params: dest ? { country: dest } : {} })).data,
  });

  const remove = async (id) => {
    await api.delete(`/cart/${id}`);
    window.dispatchEvent(new Event("cart-updated"));
    qc.invalidateQueries({ queryKey: ["cart"] });
  };

  useEffect(() => {
    const next = data?.payment?.recommended;
    if (next) setProvider(next);
  }, [data?.payment?.recommended, dest]);

  const addressReady = addr.name.trim() && addr.line1.trim() && addr.city.trim() && addr.postal.trim() && dest.length === 2;

  const checkout = async () => {
    if (!addressReady) return toast.error("Enter a complete shipping address");
    if (provider === "cashfree" && !String(addr.phone || "").trim()) {
      return toast.error("Enter a mobile number. Cashfree needs it for UPI and Indian cards.");
    }
    setChecking(true);
    try {
      if (saveAddr && updateProfile) {
        await updateProfile({
          address_name: addr.name,
          address_line1: addr.line1,
          address_line2: addr.line2,
          address_city: addr.city,
          address_region: addr.region,
          address_postal: addr.postal,
          address_country: dest,
          address_phone: addr.phone,
          country: dest,
        }).catch(() => {});
      }
      const { data: pay } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        currency,
        phone: addr.phone,
        provider,
        address: { ...addr, country: dest, phone: addr.phone },
      });
      if (pay.payment_session_id) await launchCashfree(pay);
      else window.location.href = pay.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setChecking(false);
    }
  };

  if (isLoading || importing) return <div className="max-w-4xl mx-auto px-6 pt-28 pb-24"><div className="h-64 shimmer" /></div>;

  const items = data?.items || [];
  const displayTotal = items.reduce((sum, { product, quantity }) => {
    const line = convert((product?.price || 0) * quantity, product?.currency || "USD");
    return sum + (line || 0);
  }, 0);
  const shippingDisplay = (data?.shipping_by_seller || []).reduce(
    (s, row) => s + (convert(row.fee, row.currency || "USD") || 0),
    0,
  );
  const blocked = data?.blocked || [];

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <h1 className="font-serif text-5xl tracking-tight mb-12">Your bag</h1>

      {items.length === 0 ? (
        <div className="py-20 text-center">
          <ShoppingBag className="w-8 h-8 mx-auto text-aura-muted mb-4" />
          <p className="text-aura-muted mb-6">Your bag is empty.</p>
          <Link to="/shop" className="px-8 py-3 bg-aura-accent text-white text-sm inline-block">Continue shopping</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="divide-y divide-aura-border" data-testid="cart-items">
              {items.map(({ product, quantity }) => (
                <div key={product.id} className="flex gap-5 py-6">
                  <Link to={`/product/${product.id}`} className="w-24 h-32 bg-aura-sand overflow-hidden shrink-0">
                    <img src={imgUrl(product.images?.[0])} alt={product.title} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1">
                    <div className="font-serif text-xl">{product.title}</div>
                    <div className="text-xs text-aura-muted mt-1">{product.seller_name}</div>
                    <div className="text-sm text-aura-muted mt-2">Qty {quantity}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{formatMoney((product.price || 0) * quantity, product.currency || "USD")}</div>
                    <button onClick={() => remove(product.id)} data-testid={`remove-${product.id}`} className="mt-3 text-aura-muted hover:text-aura-accent transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-aura-border bg-aura-surface p-6" data-testid="shipping-address">
              <div className="font-serif text-2xl mb-1">Shipping address</div>
              <p className="text-xs text-aura-muted mb-5">Required so we can apply domestic or international rates.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={addr.name} onChange={set("name")} placeholder="Full name" data-testid="addr-name" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.line1} onChange={set("line1")} placeholder="Address line 1" data-testid="addr-line1" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.line2} onChange={set("line2")} placeholder="Address line 2 (optional)" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.city} onChange={set("city")} placeholder="City" data-testid="addr-city" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.region} onChange={set("region")} placeholder="State / region" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.postal} onChange={set("postal")} placeholder="Postal code" data-testid="addr-postal" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={addr.phone} onChange={set("phone")} placeholder={provider === "cashfree" ? "Mobile (required for Cashfree)" : "Mobile (optional)"} data-testid="addr-phone" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <select value={addr.country} onChange={set("country")} data-testid="addr-country" className="px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <label className="mt-4 flex items-center gap-2 text-xs text-aura-muted cursor-pointer">
                <input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="accent-aura-accent" />
                Save to my profile
              </label>
              {blocked.length > 0 && (
                <p className="mt-3 text-sm text-aura-accent" data-testid="ship-blocked">Does not ship to {dest}: {blocked.join(", ")}</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-aura-surface border border-aura-border p-6 sticky top-24">
              <div className="font-serif text-2xl mb-6">Summary</div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-aura-muted">Subtotal</span>
                <span className="font-mono">{formatMoney(displayTotal, currency, { convert: false })}</span>
              </div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-aura-muted">Shipping</span>
                <span className="font-mono" data-testid="shipping-total">
                  {!addressReady ? "Enter address" : formatMoney(shippingDisplay, currency, { convert: false })}
                </span>
              </div>
              <div className="border-t border-aura-border pt-4 flex justify-between font-medium">
                <span>Total</span>
                <span className="font-mono" data-testid="cart-total">{formatMoney(displayTotal + (addressReady ? shippingDisplay : 0), currency, { convert: false })}</span>
              </div>
              <div className="mt-6">
                <PayMethodPicker
                  methods={data?.payment?.methods}
                  value={provider}
                  onChange={setProvider}
                  note={(data?.payment?.note || "").replace("from outside India", `from ${countryName || "outside India"}`)}
                />
              </div>
              <button
                onClick={checkout}
                disabled={checking || !addressReady || blocked.length > 0 || !provider}
                data-testid="checkout-btn"
                className="mt-4 w-full py-3.5 btn-gradient rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {checking && <Loader2 className="w-4 h-4 animate-spin" />}
                {provider === "cashfree" ? "Pay with Cashfree" : provider === "stripe" ? "Pay with Stripe" : "Pay"}
              </button>
              <p className="mt-3 text-[11px] text-aura-muted text-center">
                Partner catalog shops still check out on their own site.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
